// ImportPolicies — R0 Bulk Policy Intake UI (Gate 3 onboarding).
//
// Locked spec (S80 §5 / S81):
// - Multi-file picker, PDF + Word. PDF preferred; Word converts during import.
// - Per-file review table with batch-level defaults + per-row override —
//   ONE interaction pattern for ack, owner, category, and dates.
// - Ack toggle defaults OFF (BINDING — F-S78-3). Importing many ack-required
//   policies would wall every admin behind pending acknowledgments on
//   undeletable rows. The UI warns when it is switched on.
// - Idempotency key minted ONCE per file at selection time, reused on retry.
// - already_imported is rendered as success ("Already imported"), never error.

import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { AppHeader } from '@/components/layout/AppHeader';
import { BackArrow } from '@/components/BackArrow';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Upload, FileText, X, AlertTriangle, CheckCircle, Loader2, RotateCcw } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useClinicCategories } from '@/hooks/useClinicCategories';
import { useClinicStaff } from '@/hooks/useClinicStaff';
import { useMyClinicRole } from '@/hooks/useMyClinicRole';
import { useSubscriptionStatus } from '@/hooks/useSubscriptionStatus';
import {
  useBulkPolicyIntake,
  type IntakeRowOutcome,
  type IntakeFailureCode,
} from '@/hooks/useBulkPolicyIntake';
import { cn } from '@/lib/utils';

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB — matches existing upload flows
const ALLOWED_EXTENSIONS = ['pdf', 'doc', 'docx'];

type RowStatus = 'ready' | 'uploading' | 'importing' | 'imported' | 'already_imported' | 'failed';

interface ImportRow {
  idempotencyKey: string; // minted once at selection; never regenerated
  file: File;
  uploadedPath: string | null;
  name: string;
  category: string;
  effectiveDate: string;
  attestedApprovedAt: string;
  nextReviewDate: string;
  policyOwner: string;
  requiresAcknowledgement: boolean;
  status: RowStatus;
  failureCode?: IntakeFailureCode;
}

const FAILURE_LABELS: Record<IntakeFailureCode, string> = {
  upload_failed: 'File upload failed — check your connection and retry.',
  invalid_input: 'A required field is missing or invalid for this row.',
  denied: 'Your account is not permitted to import policies.',
  conversion_failed: 'Word-to-PDF conversion failed for this file.',
  error: 'Something went wrong importing this row. Retry is safe.',
};

function titleFromFileName(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.');
  const base = lastDot > 0 ? fileName.slice(0, lastDot) : fileName;
  return base.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
}

function todayIso(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

function plusMonthsIso(months: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return format(d, 'yyyy-MM-dd');
}

export default function ImportPolicies() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { role } = useMyClinicRole();
  const { data: categories = [] } = useClinicCategories();
  const { data: staff = [] } = useClinicStaff();
  const { runImport, isRunning } = useBulkPolicyIntake();
  const { data: subscriptionStatus } = useSubscriptionStatus();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Batch defaults (applied to new rows; "Apply to all" stamps existing) ──
  const [batchCategory, setBatchCategory] = useState('');
  const [batchEffectiveDate, setBatchEffectiveDate] = useState(todayIso());
  const [batchAttestedDate, setBatchAttestedDate] = useState(todayIso());
  const [batchNextReview, setBatchNextReview] = useState(plusMonthsIso(12));
  const [batchOwner, setBatchOwner] = useState<string>(''); // set lazily to importer
  const [batchAck, setBatchAck] = useState(false); // BINDING default OFF — F-S78-3

  const [rows, setRows] = useState<ImportRow[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [summary, setSummary] = useState<{ imported: number; skipped: number; failed: number } | null>(null);

  const effectiveBatchOwner = batchOwner || profile?.id || '';

  const ackOnCount = rows.filter((r) => r.requiresAcknowledgement).length;
  const doneCount = rows.filter((r) => r.status === 'imported' || r.status === 'already_imported').length;
  const failedRows = rows.filter((r) => r.status === 'failed');
  const pendingRows = rows.filter((r) => r.status === 'ready' || r.status === 'failed');

  const canImport = useMemo(() => {
    if (isRunning || pendingRows.length === 0) return false;
    return pendingRows.every(
      (r) =>
        r.name.trim().length > 0 &&
        r.category.length > 0 &&
        r.effectiveDate &&
        r.attestedApprovedAt &&
        r.nextReviewDate &&
        r.policyOwner,
    );
  }, [isRunning, pendingRows]);

  function addFiles(files: FileList | File[]) {
    const next: ImportRow[] = [];
    let rejected: string | null = null;

    Array.from(files).forEach((file) => {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        rejected = 'Only PDF and Word documents (.pdf, .doc, .docx) are accepted.';
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        rejected = `${file.name} is larger than 100MB.`;
        return;
      }
      next.push({
        idempotencyKey: crypto.randomUUID(), // minted once — reused on any retry
        file,
        uploadedPath: null,
        name: titleFromFileName(file.name),
        category: batchCategory,
        effectiveDate: batchEffectiveDate,
        attestedApprovedAt: batchAttestedDate,
        nextReviewDate: batchNextReview,
        policyOwner: effectiveBatchOwner,
        requiresAcknowledgement: batchAck,
        status: 'ready',
      });
    });

    setFileError(rejected);
    if (next.length > 0) {
      setRows((prev) => [...prev, ...next]);
      setSummary(null);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function updateRow(key: string, patch: Partial<ImportRow>) {
    setRows((prev) => prev.map((r) => (r.idempotencyKey === key ? { ...r, ...patch } : r)));
  }

  function removeRow(key: string) {
    setRows((prev) => prev.filter((r) => r.idempotencyKey !== key));
  }

  function applyDefaultsToAll() {
    setRows((prev) =>
      prev.map((r) =>
        r.status === 'imported' || r.status === 'already_imported'
          ? r
          : {
              ...r,
              category: batchCategory || r.category,
              effectiveDate: batchEffectiveDate,
              attestedApprovedAt: batchAttestedDate,
              nextReviewDate: batchNextReview,
              policyOwner: effectiveBatchOwner,
              requiresAcknowledgement: batchAck,
            },
      ),
    );
  }

  async function handleImport() {
    const targets = rows.filter((r) => r.status === 'ready' || r.status === 'failed');
    if (targets.length === 0) return;
    setSummary(null);

    const outcomes: IntakeRowOutcome[] = await runImport(
      targets.map((r) => ({
        idempotencyKey: r.idempotencyKey,
        file: r.file,
        uploadedPath: r.uploadedPath,
        name: r.name.trim(),
        category: r.category,
        effectiveDate: r.effectiveDate,
        nextReviewDate: r.nextReviewDate,
        policyOwner: r.policyOwner,
        attestedApprovedAt: r.attestedApprovedAt,
        requiresAcknowledgement: r.requiresAcknowledgement,
      })),
      (key, phase) => updateRow(key, { status: phase }),
    );

    let imported = 0;
    let skipped = 0;
    let failed = 0;
    outcomes.forEach((o) => {
      if (o.status === 'imported') imported++;
      else if (o.status === 'already_imported') skipped++;
      else failed++;
      updateRow(o.idempotencyKey, {
        status: o.status,
        failureCode: o.failureCode,
        uploadedPath: o.uploadedPath ?? null,
      });
    });
    setSummary({ imported, skipped, failed });
  }

  // Server enforces the real boundary (RLS + edge role check); this is UX only.
  if (role === 'staff') {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <main className="container max-w-2xl py-10">
          <p className="text-muted-foreground">Policy import is available to clinic administrators.</p>
        </main>
      </div>
    );
  }

  // Soft-Close: import is a paid-only surface (F-S81-2, mirrors Composer).
  // The import_policy_as_approved RPC already refuses soft-closed writes.
  if (subscriptionStatus?.status === 'soft_closed') {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <div className="flex min-h-[calc(100vh-4rem)] flex-col items-center justify-center bg-background px-6 text-center gap-4">
          <p className="text-base font-semibold text-foreground">Import is not available</p>
          <p className="text-sm text-muted-foreground max-w-sm">
            Your subscription has ended. Reactivate to import policies.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="container max-w-6xl py-8 space-y-6">
        <div>
          <BackArrow />
          <h1 className="text-2xl md:text-[32px] font-extrabold tracking-tight mt-2">Import Existing Policies</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">
            Each file becomes an approved, locked policy — your original approval date preserved.
          </p>
        </div>

        {/* File picker */}
        <div className="rounded-[8px] border border-border bg-card p-6">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx"
          onChange={(e) => e.target.files && addFiles(e.target.files)}
          className="hidden"
          id="import-file-picker"
        />
        <label
          htmlFor="import-file-picker"
          onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
          onDragEnter={(e) => { e.preventDefault(); setIsDragOver(true); }}
          onDragLeave={(e) => { e.preventDefault(); setIsDragOver(false); }}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragOver(false);
            if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
          }}
          className={cn(
            'flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors',
            isDragOver ? 'border-primary bg-primary/5' : 'hover:bg-muted/50',
          )}
        >
          <Upload className="h-6 w-6 text-muted-foreground mb-1.5" />
          <span className="text-sm text-muted-foreground">Click or drag files to add them</span>
          <span className="text-xs text-muted-foreground mt-0.5">PDF or Word, up to 100MB each · Word files convert to PDF during import</span>
        </label>

        {fileError && (
          <p className="mt-3 flex items-center gap-2 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4" /> {fileError}
          </p>
        )}
        </div>

        {rows.length > 0 && (
          <>
            {/* Batch defaults bar */}
            <div className="rounded-[8px] border border-border bg-card p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold">Defaults for Every File</h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={applyDefaultsToAll}
                  disabled={isRunning}
                  className="border-primary/40 text-primary hover:bg-primary/5 hover:text-primary"
                >
                  Apply to all rows
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Category</Label>
                  <Select value={batchCategory} onValueChange={setBatchCategory} disabled={isRunning}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Effective date</Label>
                  <Input type="date" value={batchEffectiveDate} onChange={(e) => setBatchEffectiveDate(e.target.value)} disabled={isRunning} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Approved by clinic on</Label>
                  <Input type="date" max={todayIso()} value={batchAttestedDate} onChange={(e) => setBatchAttestedDate(e.target.value)} disabled={isRunning} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Next review</Label>
                  <Input type="date" value={batchNextReview} onChange={(e) => setBatchNextReview(e.target.value)} disabled={isRunning} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Owner</Label>
                  <Select value={effectiveBatchOwner} onValueChange={setBatchOwner} disabled={isRunning}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {staff.map((m) => (
                        <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Require acknowledgment</Label>
                  <div className="flex items-center gap-2 h-10">
                    <Checkbox
                      id="batch-ack"
                      checked={batchAck}
                      onCheckedChange={(c) => setBatchAck(c === true)}
                      disabled={isRunning}
                    />
                    <Label htmlFor="batch-ack" className="text-xs font-normal cursor-pointer">
                      {batchAck ? 'On' : 'Off'}
                    </Label>
                  </div>
                </div>
              </div>
              {batchAck && (
                <p className="text-xs text-muted-foreground">
                  Every acknowledgment-required policy appears on every assigned member's pending
                  list the moment it imports. Recommended: leave off, enable per policy later.
                </p>
              )}
            </div>

            {ackOnCount > 3 && (
              <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>
                  {ackOnCount} policies will require acknowledgment the moment they import. Team
                  members — including you — will see all of them as pending on their next visit.
                  Imported policies are permanent, so this cannot be bulk-undone afterward.
                </span>
              </div>
            )}

            {/* Review rows — one card per file, fields wrap on narrow screens */}
            <div className="space-y-3">
              {rows.map((row) => {
                const locked = row.status === 'imported' || row.status === 'already_imported' || isRunning;
                return (
                  <div key={row.idempotencyKey} className="rounded-[8px] border border-border bg-card p-4">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="min-w-0 flex-1">
                        <Input
                          value={row.name}
                          onChange={(e) => updateRow(row.idempotencyKey, { name: e.target.value })}
                          disabled={locked}
                          className={cn('h-9 font-medium', !row.name.trim() && 'border-destructive')}
                        />
                        <span className="mt-1.5 flex items-center gap-1 text-xs text-muted-foreground">
                          <FileText className="h-3 w-3" /> {row.file.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {row.status === 'ready' && <Badge variant="outline">Ready</Badge>}
                        {row.status === 'uploading' && (
                          <Badge variant="secondary" className="gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Uploading</Badge>
                        )}
                        {row.status === 'importing' && (
                          <Badge variant="secondary" className="gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Importing</Badge>
                        )}
                        {row.status === 'imported' && (
                          <Badge className="gap-1 bg-[#339933] hover:bg-[#339933]"><CheckCircle className="h-3 w-3" /> Imported</Badge>
                        )}
                        {row.status === 'already_imported' && (
                          <Badge variant="secondary" className="gap-1"><CheckCircle className="h-3 w-3" /> Already imported</Badge>
                        )}
                        {row.status === 'failed' && (
                          <Badge variant="destructive" className="gap-1"><AlertTriangle className="h-3 w-3" /> Failed</Badge>
                        )}
                        {(row.status === 'ready' || row.status === 'failed') && !isRunning && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeRow(row.idempotencyKey)}>
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Category</Label>
                        <Select
                          value={row.category}
                          onValueChange={(v) => updateRow(row.idempotencyKey, { category: v })}
                          disabled={locked}
                        >
                          <SelectTrigger className={cn('h-9', !row.category && 'border-destructive')}>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map((c) => (
                              <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Effective date</Label>
                        <Input type="date" className="h-9" value={row.effectiveDate} disabled={locked}
                          onChange={(e) => updateRow(row.idempotencyKey, { effectiveDate: e.target.value })} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Approved by clinic on</Label>
                        <Input type="date" className="h-9" max={todayIso()} value={row.attestedApprovedAt} disabled={locked}
                          onChange={(e) => updateRow(row.idempotencyKey, { attestedApprovedAt: e.target.value })} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Next review</Label>
                        <Input type="date" className="h-9" value={row.nextReviewDate} disabled={locked}
                          onChange={(e) => updateRow(row.idempotencyKey, { nextReviewDate: e.target.value })} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Owner</Label>
                        <Select
                          value={row.policyOwner}
                          onValueChange={(v) => updateRow(row.idempotencyKey, { policyOwner: v })}
                          disabled={locked}
                        >
                          <SelectTrigger className="h-9"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>
                            {staff.map((m) => (
                              <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Require acknowledgment</Label>
                        <div className="flex items-center gap-2 h-9">
                          <Checkbox
                            checked={row.requiresAcknowledgement}
                            onCheckedChange={(c) => updateRow(row.idempotencyKey, { requiresAcknowledgement: c === true })}
                            disabled={locked}
                          />
                          <Label className="text-xs font-normal">
                            {row.requiresAcknowledgement ? 'On' : 'Off'}
                          </Label>
                        </div>
                      </div>
                    </div>

                    {row.status === 'failed' && (
                      <p className="mt-3 text-xs text-destructive">
                        {row.failureCode ? FAILURE_LABELS[row.failureCode] : FAILURE_LABELS.error}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Footer actions */}
            <div className="flex items-center justify-between gap-4">
              <div className="text-sm text-muted-foreground">
                {rows.length} file{rows.length === 1 ? '' : 's'}
                {doneCount > 0 && <> · {doneCount} completed</>}
                {failedRows.length > 0 && <span className="text-destructive"> · {failedRows.length} failed</span>}
              </div>
              <div className="flex gap-2">
                {summary && summary.failed === 0 && (
                  <Button variant="outline" onClick={() => navigate('/policies')}>View policies</Button>
                )}
                <Button onClick={handleImport} disabled={!canImport}>
                  {isRunning ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Importing…</>
                  ) : failedRows.length > 0 && failedRows.length === pendingRows.length ? (
                    <><RotateCcw className="mr-2 h-4 w-4" /> Retry failed</>
                  ) : (
                    <>Import {pendingRows.length} polic{pendingRows.length === 1 ? 'y' : 'ies'} as approved</>
                  )}
                </Button>
              </div>
            </div>

            {summary && (
              <div className="rounded-md border bg-muted/30 p-4 text-sm">
                <span className="font-medium">Import complete:</span>{' '}
                {summary.imported} imported
                {summary.skipped > 0 && <>, {summary.skipped} already imported (no duplicates created)</>}
                {summary.failed > 0 ? (
                  <span className="text-destructive">, {summary.failed} failed — fix the rows above and retry. Retrying is always safe; nothing imports twice.</span>
                ) : (
                  <>. Every policy is now locked, immutable, and part of your audit record.</>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
